import type { Dispatch, SetStateAction } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { WithChildren } from "../../../_metronic/helpers";
import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { useUser } from "../../contexts/userContext";
import type { IBillingInfo } from "../../hooks/reactQueryHooks/useBilling";
import { refreshTokenAsync } from "../../pages/auth/components/AzureLoginHelper";
interface OrganizationContextProps {
  organizations: IOrganization[] | null;
  currentOrganization: IOrganization | null;
  accessAllowed: boolean;
  isOwner: boolean;
  setAccessAllowed: Dispatch<SetStateAction<boolean>>;
  setOrganizations: Dispatch<SetStateAction<IOrganization[] | null>>;
  updateOrganization: (organization: IOrganization) => void;
  setCurrentOrganization: (organization: IOrganization) => void;
  setSubscriptionData: (data: IBillingInfo) => void;
}

export interface IOrganization {
  id: string;
  name: string;
  externalMsId?: string;
  created: string;
  isOwner?: boolean;
  owner: {
    id: string;
    email: string;
  };
  subscription?: {
    isAccessAllowed: boolean;
  };
  isPublicBalancerEnabled: boolean;
  subscriptionInfo: IBillingInfo | null;
}
const initOrganizationsContextPropsState = {
  organizations: null,
  currentOrganization: null,
  accessAllowed: true,
  isOwner: false,
  setAccessAllowed: () => {},
  setOrganizations: () => {},
  updateOrganization: () => {},
  setCurrentOrganization: () => {},
  setSubscriptionData: () => {},
};

const OrganizationContext = createContext<OrganizationContextProps>(
  initOrganizationsContextPropsState,
);

const useOrganizationContext = () => {
  return useContext(OrganizationContext);
};

const OrganizationProvider: React.FC<WithChildren> = ({ children }) => {
  const [organizations, setOrganizations] = useState<IOrganization[] | null>(
    null,
  );
  const [accessAllowed, setAccessAllowed] = useState(true);
  const [currentOrganization, setCurrentOrganization] =
    useState<IOrganization | null>(null);
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false);
  const [isOwner, setIsOwner] = useState(true);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setUserOrganizations, user } = useUser();

  const setOrg = useCallback(
    async (data: any) => {
      if (data.id === currentOrganization?.id) return;
      setIsSwitchingOrg(true);
      await refreshTokenAsync();
      setCurrentOrganization(data);
      if (pathname !== "/safety-compliance/cache") {
        navigate("/safety-compliance/cache");
      }
      setIsSwitchingOrg(false);
    },
    [
      setIsSwitchingOrg,
      pathname,
      setCurrentOrganization,
      navigate,
      currentOrganization,
    ],
  );
  const updateOrganization = useCallback(
    (organization: IOrganization) => {
      setOrganizations(prev => {
        if (!prev) return null;
        const index = prev.findIndex(org => org.id === organization.id);
        if (index === -1) return prev;
        const newOrganizations = [...prev];
        newOrganizations[index] = organization;
        return newOrganizations;
      });
    },
    [setOrganizations],
  );

  const setSubscriptionData = (data: IBillingInfo) => {
    setCurrentOrganization(prev => {
      if (!prev) return null;
      return {
        ...prev,
        subscriptionInfo: data,
      };
    });
  };

  useEffect(() => {
    if (!currentOrganization) return;
    localStorage.setItem(
      "currentOrganization",
      JSON.stringify(currentOrganization),
    );
  }, [currentOrganization]);

  useEffect(() => {
    if (user && currentOrganization) {
      const orgFromProfile = user.organization.find(
        (org: any) => org.id === currentOrganization.id,
      );

      setAccessAllowed(orgFromProfile?.subscription?.isAccessAllowed || false);
      setIsOwner(orgFromProfile?.isOwner || false);
    }
  }, [user, currentOrganization]);

  useEffect(() => {
    if (!organizations) return;
    setUserOrganizations(organizations);
    const currentOrg = localStorage.getItem("currentOrganization");

    if (currentOrg) {
      const parsedOrg = JSON.parse(currentOrg);
      const orgById = organizations.find(org => org.id === parsedOrg.id);
      if (orgById) {
        setCurrentOrganization(orgById);
        localStorage.setItem("currentOrganization", JSON.stringify(orgById));
        return;
      }
      setCurrentOrganization(organizations[0]);
      return;
    }
    setCurrentOrganization(organizations[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations]);

  const value = useMemo(() => {
    return {
      organizations,
      isOwner,
      setOrganizations,
      updateOrganization: updateOrganization,
      setCurrentOrganization: setOrg,
      currentOrganization: currentOrganization,
      accessAllowed: accessAllowed,
      setAccessAllowed: setAccessAllowed,
      setSubscriptionData: setSubscriptionData,
    };
  }, [
    organizations,
    isOwner,
    currentOrganization,
    accessAllowed,
    setAccessAllowed,
    updateOrganization,
    setOrg,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
      {isSwitchingOrg && <LoadingOverlay />}
    </OrganizationContext.Provider>
  );
};

export { OrganizationProvider, useOrganizationContext };
